import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { UserSelect, useMembers } from '../ui/UserSelect';
import { assignConversation, handoffConversation, listQueues } from './api';

/**
 * Who is answering this, and how it gets passed on (context.md §45–§56). Phase 139.
 *
 * The panel used to state the assignee and stop there — the code even said "not editable here
 * yet" — so the whole flow existed in the API and nowhere a person stands: a conversation arrived
 * unassigned and there was no way to take it, hand it to a colleague, or move it to another queue
 * without leaving the screen.
 *
 * Four actions, and they are deliberately not one dropdown:
 *
 * - **Atender** is the common one, it is one click, and it is what somebody does forty times a day
 * - **Atribuir a** is for handing a thread to a named person
 * - **Transferir para fila** moves it to another team, letting that queue's own strategy choose who
 *   — "passar para o suporte" is a different act from "passar para a Ana"
 * - **Devolver** puts it back unassigned, which is what happens when somebody picks up a thread
 *   they cannot finish
 */

export function AssignmentControls({
  conversationId,
  assigneeId,
  queueId,
}: {
  conversationId: string;
  assigneeId: string | null;
  queueId: string | null;
}): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();
  const { options: people } = useMembers();
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queues = useQuery({ queryKey: ['queues'], queryFn: listQueues, enabled: transferring });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
    void client.invalidateQueries({ queryKey: ['inbox'] });
  };

  const assign = useMutation({
    mutationFn: (to: string | null) => assignConversation(conversationId, to),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const transfer = useMutation({
    mutationFn: (queue: string) =>
      // No assignee: the queue's strategy picks, which is the point of moving it to a queue rather
      // than to a person.
      handoffConversation(conversationId, { queueId: queue }),
    onSuccess: () => {
      setError(null);
      setTransferring(false);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const mine = assigneeId !== null && assigneeId === user?.id;
  const assignee = people.find((person) => person.id === assigneeId);

  return (
    <div className="assignment">
      <span className="contact-field__label">{t('inbox.assignee')}</span>

      <UserSelect
        value={assigneeId}
        options={people}
        onChange={(next) => assign.mutate(next)}
        disabled={assign.isPending}
        placeholder={t('inbox.unassigned')}
        emptyLabel={t('inbox.unassigned')}
      />

      <div className="assignment__actions">
        {/* The one somebody does forty times a day, and it is one click. */}
        {!mine && (
          <button
            type="button"
            className="button button--primary button--compact"
            disabled={assign.isPending || !user}
            onClick={() => assign.mutate(user!.id)}
          >
            {t('inbox.takeIt')}
          </button>
        )}

        {assignee && (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={assign.isPending}
            onClick={() => assign.mutate(null)}
          >
            {t('inbox.giveBack')}
          </button>
        )}

        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setTransferring((current) => !current)}
        >
          {t('inbox.transferToQueue')}
        </button>
      </div>

      {transferring && (
        <div className="assignment__queues">
          {queues.isLoading ? (
            <span className="muted small">{t('state.loading')}</span>
          ) : (
            <div className="chips">
              {(queues.data?.queues ?? [])
                .filter((queue) => queue.id !== queueId)
                .map((queue) => (
                  <button
                    key={queue.id}
                    type="button"
                    className="chip"
                    disabled={transfer.isPending}
                    onClick={() => transfer.mutate(queue.id)}
                  >
                    {queue.name}
                  </button>
                ))}
            </div>
          )}

          {(queues.data?.queues ?? []).length <= 1 && !queues.isLoading && (
            <span className="muted small">{t('inbox.noOtherQueue')}</span>
          )}
        </div>
      )}

      {error && (
        <p className="alert alert--compact" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
